import { SceneService } from '../../services/SceneService';
import { PunchListTrackManager } from '../lib/PunchListTrackManager';
import { VideoTranscriptElement } from '../models/elements/VideoTranscriptElement';
import { computeCoverCrop } from '../models/layout/CropTransformUtils';
import { AbstractElementCommand } from './AbstractCommand';
import { CreateScenesForRestoredRegionCommand } from './CreateScenesForRestoredRegionCommand';
import { TimelineMode } from './TimelineMode';

import type {
  FaceDetection,
  ISceneFramingService,
} from '../../services/framing/SmartFramingService';
import type {
  ExtendStartInfo,
  TrimStartInfo,
} from '../../services/SceneService';
import type { Element } from '../models/Element';
import type { NaturalDimensions } from '../types/aspectRatio';
import type { SceneDetection } from '../types/sceneDetection';
import type { VideoEditor } from '../VideoEditor';

/**
 * Command to trim the start of an element (adjusts start time and duration)
 * Uses direct object references
 */
export class TrimElementStartCommand extends AbstractElementCommand {
  private newStartTime: number;
  private snapToFrame: boolean;

  getName(): string {
    return 'TrimElementStartCommand';
  }
  private originalState: Record<string, any>;
  private sceneService: SceneService;
  private trimStartInfo: TrimStartInfo | null = null;
  private extendStartInfo: ExtendStartInfo | null = null;
  private readonly sceneDetections: SceneDetection[] | undefined;
  private readonly faceDetections: FaceDetection[] | undefined;
  private readonly framingService: ISceneFramingService | undefined;
  private readonly naturalMediaDimensions:
    | Record<string, NaturalDimensions>
    | undefined;
  private createScenesSubCommand: CreateScenesForRestoredRegionCommand | null =
    null;

  /**
   * Creates a new TrimElementStartCommand
   * @param editor The video editor instance
   * @param element The element to trim
   * @param newStartTime The new start time for the element (in seconds)
   * @param snapToFrame Whether to snap the time to frame boundaries
   * @param sceneDetections Optional scene detections for creating proper scenes when extending
   * @param naturalMediaDimensions Optional natural dimensions for computing crop
   * @param faceDetections Optional face detections for smart framing
   * @param framingService Optional framing service for calculating per-scene crops
   */
  constructor(
    editor: VideoEditor,
    element: Element,
    newStartTime: number,
    snapToFrame: boolean = true,
    sceneDetections?: SceneDetection[],
    naturalMediaDimensions?: Record<string, NaturalDimensions>,
    faceDetections?: FaceDetection[],
    framingService?: ISceneFramingService,
  ) {
    super(editor, element);

    // Ensure time is never negative and apply frame snapping
    this.newStartTime = Math.max(
      0,
      snapToFrame ? this.snapTimeToFrame(newStartTime) : newStartTime,
    );
    this.snapToFrame = snapToFrame;
    this.sceneDetections = sceneDetections;
    this.naturalMediaDimensions = naturalMediaDimensions;
    this.faceDetections = faceDetections;
    this.framingService = framingService;

    // Capture full element state for undo
    this.originalState = this.captureElementState(this.sourceElement);

    // Initialize scene service
    this.sceneService = new SceneService();

    // Capture scene adjustment info BEFORE modification (only for transcript elements)
    if (this.isTranscriptElement()) {
      this.captureTrimStartInfo();
    }

    // Store affected elements for undo in ripple mode
    if (this.getTimelineMode() === TimelineMode.RIPPLE) {
      this.storeAffectedElements({});
    }
  }

  execute(): boolean {
    const isTranscriptElem = this.isTranscriptElement();
    const result = this.performTrimOperation();

    if (result) {
      // Adjust scenes AFTER element modification (only for transcript elements)
      if (isTranscriptElem) {
        if (this.trimStartInfo) {
          // Trimming: start moved later
          this.sceneService.adjustScenesAfterTrimStart(
            this.editor.timeline,
            this.trimStartInfo,
          );
        } else if (this.extendStartInfo) {
          // Extending: start moved earlier
          this.sceneService.adjustScenesAfterExtendStart(
            this.editor.timeline,
            this.extendStartInfo,
          );

          // Create scenes for the restored portion if scene detections available
          this.createScenesForExtendedRegion();
        }
      }

      // Recalculate punch list position before global reconciliation so the
      // updated timing is committed ahead of reconcilePunchList.
      const trackManager = this.editor.getTrackManager();
      const isPunchListElement = trackManager.isPunchListElement(
        this.sourceElement.id,
      );
      if (!isTranscriptElem && isPunchListElement) {
        const punchListTrackManager = new PunchListTrackManager(this.editor);
        punchListTrackManager.recalculatePunchListPosition([
          this.sourceElement.id,
        ]);
      }

      this.recalculateTimeline();
    }

    return result;
  }

  /**
   * Performs the trim operation using the appropriate track manager
   * @returns true if the operation was successful
   */
  private performTrimOperation(): boolean {
    if (this.isTranscriptElement()) {
      return this.trimTranscriptElement();
    } else {
      return this.trimRegularElement();
    }
  }

  /**
   * Checks if the source element is a VideoTranscriptElement
   */
  private isTranscriptElement(): boolean {
    return this.sourceElement instanceof VideoTranscriptElement;
  }

  /**
   * Trims a VideoTranscriptElement using TranscriptTrackManager
   */
  private trimTranscriptElement(): boolean {
    const transcriptTrackManager = this.editor.getTranscriptTrackManager();
    // Use the active TimelineMode for both trims and extensions. The
    // underlying TrackManager.trimElementStart handles ripple-extend
    // natively — same mechanism the transcript restore commands rely on.
    const clipResult = transcriptTrackManager.trimElementStart(
      this.sourceElement as VideoTranscriptElement,
      this.newStartTime,
      this.getTimelineMode(),
      this.snapToFrame,
    );
    return clipResult !== null;
  }

  /**
   * Trims a regular element using TrackManager
   */
  private trimRegularElement(): boolean {
    const trackManager = this.editor.getTrackManager();
    return trackManager.trimElementStart(
      this.sourceElement,
      this.newStartTime,
      TimelineMode.DEFAULT, // instead of this.getTimelineMode(), always default for non-transcript elements
      this.snapToFrame,
    );
  }

  undo(): boolean {
    // Undo scene creation sub-command first (reverse order)
    if (this.createScenesSubCommand) {
      this.createScenesSubCommand.undo();
    }

    // Restore the complete original element state
    this.restoreElementState(this.sourceElement, this.originalState);

    // Restore affected elements if in ripple mode
    this.restoreAffectedElements();

    // Recalculate timeline duration
    this.recalculateTimeline();

    return true;
  }

  getDescription(): string {
    return `Trim start of element ${this.sourceElement.id} to ${this.newStartTime}s`;
  }

  /**
   * Captures trim/extend start information for scene adjustment
   * Must be called BEFORE element is modified
   * Assumes element is VideoTranscriptElement (checked by caller)
   */
  private captureTrimStartInfo(): void {
    if (this.sourceElement instanceof VideoTranscriptElement === false) {
      return;
    }

    const transcriptElement = this.sourceElement as VideoTranscriptElement;
    const originalMediaStart = transcriptElement.getMediaStartTime();
    const originalMediaEnd = transcriptElement.getMediaEndTime();

    // Calculate how much is being trimmed/extended from the start
    const delta = this.newStartTime - this.sourceElement.time;
    const newMediaStart = originalMediaStart + delta;

    if (delta > 0) {
      // Trimming: start moved later
      this.trimStartInfo = {
        elementId: this.sourceElement.id,
        originalMediaStart,
        originalMediaEnd,
        newMediaStart,
      };
    } else if (delta < 0) {
      // Extending: start moved earlier
      this.extendStartInfo = {
        elementId: this.sourceElement.id,
        originalMediaStart,
        originalMediaEnd,
        newMediaStart,
      };
    }
    // If delta === 0, no adjustment needed
  }

  /**
   * Creates scenes for the extended (restored) region when scene detections are available.
   * Called when the element start is moved earlier (extending).
   */
  private createScenesForExtendedRegion(): void {
    if (
      !this.sceneDetections ||
      this.sceneDetections.length === 0 ||
      !this.extendStartInfo
    ) {
      return;
    }

    const transcriptTrack = this.editor.getVideoTranscriptTrack();
    if (!transcriptTrack) {
      return;
    }

    // The restored region is from newMediaStart to originalMediaStart
    const { newMediaStart, originalMediaStart } = this.extendStartInfo;

    // Compute crop for the element
    const crop = this.computeCoverCropForElement();

    // Create sub-command (don't create its own undo entry)
    this.createScenesSubCommand = new CreateScenesForRestoredRegionCommand(
      this.editor.timeline,
      this.sceneDetections,
      {
        elements: [this.sourceElement],
        mediaStartTime: newMediaStart,
        mediaEndTime: originalMediaStart,
      },
      transcriptTrack.id,
      false, // Don't create undo entry - parent command handles undo
      crop ?? undefined,
      this.framingService,
      this.faceDetections,
    );

    // Execute directly (not through executeCommand)
    this.createScenesSubCommand.execute();
  }

  /**
   * Computes a cover crop for the source element.
   * Returns a centered crop that fills the canvas while maintaining aspect ratio.
   */
  private computeCoverCropForElement(): {
    x: number;
    y: number;
    w: number;
    h: number;
  } | null {
    if (!this.naturalMediaDimensions) {
      return null;
    }

    const element = this.sourceElement;
    if (!(element instanceof VideoTranscriptElement)) {
      return null;
    }

    const sourceUrl = element.source;
    const naturalDimensions = this.naturalMediaDimensions[sourceUrl];

    if (!naturalDimensions) {
      return null;
    }

    return computeCoverCrop(
      this.editor.timeline.width,
      this.editor.timeline.height,
      naturalDimensions.width,
      naturalDimensions.height,
    );
  }
}
